import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaTrash, FaPlus, FaMoneyBillWave } from "react-icons/fa";
import DynamicSelect from "../../DynamicSelect";
import { useAuth } from "../../../utils/AuthContext";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import AccountExportOptions from "../../AccountExportOptions";
import Pagination from "../../Shared/Pagination";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const EXPENSE_API = "https://api.muktihospital.com/api/pathology-other-expense";
const PATHOLOGISTS_API = "https://api.muktihospital.com/api/pathology-accounts/pathologists-list";
const LIVE_CASH_API = "https://api.muktihospital.com/api/pathology-accounts/live-cash";

const CATEGORY_OPTIONS = [
  { value: "Reagent", label: "Reagent / Chemical" },
  { value: "Repair", label: "Repair / Servicing" },
  { value: "Utility", label: "Utility (Electricity/Water)" },
  { value: "Other", label: "Other" },
];

const PathologyOtherExpense = () => {
  const { user } = useAuth();

  const [form, setForm] = useState({
    category: "Reagent",
    amount: "",
    note: "",
    expenseDate: new Date().toISOString().split("T")[0],
    paidBy: "GENERAL",
  });
  const [submitting, setSubmitting] = useState(false);

  const [staffOptions, setStaffOptions] = useState([{ value: "GENERAL", label: "General (Lab Fund)" }]);
  const [cashInHand, setCashInHand] = useState(null);
  const [loadingCash, setLoadingCash] = useState(false);

  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filterType, setFilterType] = useState("daily");
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split("T")[0]);
  const [filterCategory, setFilterCategory] = useState("All");
  const [deleteId, setDeleteId] = useState(null);
  const [filterUser, setFilterUser] = useState("ALL");
  const [dropdownOpen, setDropdownOpen] = useState(false);const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (user?.name) {
      setForm((prev) => ({ ...prev, paidBy: user.name }));
      setFilterUser(user.name);
    }
  }, [user?.name]);

  // Load staff for "Paid By"
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(PATHOLOGISTS_API, { headers: { "x-api-key": API_KEY } });
        const list = res.data.data || [];
        setStaffOptions([
          { value: "GENERAL", label: "General (Lab Fund)" },
          ...list.map((p) => ({ value: p.name, label: p.name })),
        ]);
      } catch (err) {
        console.error("Load staff error:", err);
      }
    })();
  }, []);

  // Fetch live cash for the selected payer
  const fetchLiveCash = useCallback(async (paidByUser) => {
    setLoadingCash(true);
    try {
      const res = await axios.get(LIVE_CASH_API, {
        headers: { "x-api-key": API_KEY },
        params: { userName: paidByUser || "GENERAL" }
      });
      setCashInHand(res.data.availableCash ?? 0);
    } catch (err) {
      console.error("Fetch live cash error:", err);
      setCashInHand(0);
    } finally {
      setLoadingCash(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveCash(form.paidBy);
  }, [form.paidBy, fetchLiveCash]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: filterType };
      if (filterType !== "all") params.date = filterDate;
      if (filterCategory !== "All") params.category = filterCategory;
      const res = await axios.get(`${EXPENSE_API}/list`, { headers: { "x-api-key": API_KEY }, params });
      setExpenses(res.data.expenses || []);
      setTotal(res.data.total || 0);
      setCount(res.data.count || 0);
    } catch (err) {
      console.error("Fetch expenses error:", err);
      toast.error("Failed to load expenses.");
    } finally {
      setLoading(false);
    }
  }, [filterType, filterDate, filterCategory]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleAdd = async () => {
    const expenseAmount = Number(form.amount);
    if (!form.amount || expenseAmount <= 0) return toast.error("Enter a valid amount.");

    if (cashInHand !== null && (cashInHand <= 0 || expenseAmount > cashInHand)) {
      return toast.error(`Insufficient Cash in Hand! Available: ৳${cashInHand.toFixed(2)}, Requested: ৳${expenseAmount.toFixed(2)}`);
    }

    setSubmitting(true);
    try {
      await axios.post(`${EXPENSE_API}/add`, {
        category: form.category,
        amount: expenseAmount,
        note: form.note,
        expenseDate: form.expenseDate,
        userName: form.paidBy === "GENERAL" ? null : form.paidBy,
      }, { headers: { "x-api-key": API_KEY } });

      toast.success("Expense recorded.");
      setForm((prev) => ({ ...prev, amount: "", note: "" }));
      fetchExpenses();
      fetchLiveCash(form.paidBy);
    } catch (err) {
      console.error("Add expense error:", err);
      toast.error(err.response?.data?.error || "Failed to record expense.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${EXPENSE_API}/${id}`, { headers: { "x-api-key": API_KEY } });
      toast.success("Expense deleted.");
      setDeleteId(null);
      fetchExpenses();
      fetchLiveCash(form.paidBy);
    } catch (err) {
      console.error("Delete expense error:", err);
      toast.error("Failed to delete.");
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterDate, filterCategory, filterUser]);

  const totalPages = Math.ceil(
    expenses.filter((ex) => {
      if (filterUser === "ALL") return true;
      if (filterUser === "GENERAL") return !ex.userName;
      return ex.userName === filterUser;
    }).length / itemsPerPage
  ) || 1;

  const displayExpenses = expenses.filter((ex) => {
    if (filterUser === "ALL") return true;
    if (filterUser === "GENERAL") return !ex.userName;
    return ex.userName === filterUser;
  });

  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return displayExpenses.slice(startIndex, startIndex + itemsPerPage);
  }, [displayExpenses, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  const displayTotal = displayExpenses.reduce((s, ex) => s + (Number(ex.amount) || 0), 0);

  const filterUserOptions = [{ value: "ALL", label: "All Staff + General" }, ...staffOptions];

  const exportHeaders = ["SL", "Date", "Category", "Paid By", "Note", "Amount"];
  const exportDataMapper = (ex) => {
    const idx = displayExpenses.findIndex((x) => x.id === ex.id);
    return [
      idx + 1,
      ex.expenseDate ? new Date(ex.expenseDate).toLocaleDateString("en-GB") : "N/A",
      ex.category || "-",
      ex.userName || "General (Lab Fund)",
      ex.note || "-",
      (Number(ex.amount) || 0).toFixed(2),
    ].join("\t");
  };

  const getExpenseSubtitle = () => {
    const userLabel =
      filterUser === "ALL"
        ? "All Staff"
        : filterUser === "GENERAL"
        ? "General (Lab Fund)"
        : filterUser;

    if (filterType === "daily") {
      const fDate = new Date(filterDate).toLocaleDateString("en-GB");
      return `Date: ${fDate}  |  Staff: ${userLabel}  |  Category: ${filterCategory}`;
    }
    if (filterType === "monthly") {
      const dateObj = new Date(filterDate);
      const fMonth = dateObj.toLocaleString("en-US", { month: "long", year: "numeric" });
      return `Month: ${fMonth}  |  Staff: ${userLabel}  |  Category: ${filterCategory}`;
    }
    return `Period: All Time  |  Staff: ${userLabel}  |  Category: ${filterCategory}`;
  };

  const expenseFooter = [
    [
      { content: "TOTAL", colSpan: 5, styles: { fontStyle: "bold", halign: "left" } },
      { content: displayTotal.toFixed(2), styles: { fontStyle: "bold", halign: "left" } },
    ],
  ];

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header */}
        <div className="border-b border-dashed border-M-text-color/50 p-4">
          <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            <FaMoneyBillWave className="text-M-primary-color" /> Others Expense — Pathology
          </h2>
          <p className="text-xs text-gray-400 mt-1">Reagent, repair, utility and other lab costs.</p>
        </div>

        {/* Add form */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-start border-b border-gray-100">
          <div>
            <label className="text-xs font-bold block mb-1">Category</label>
            <DynamicSelect options={CATEGORY_OPTIONS}
              value={CATEGORY_OPTIONS.find((o) => o.value === form.category) || null}
              onChange={(o) => setForm({ ...form, category: o?.value || "Reagent" })} isSearchable={false} />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs font-bold block">Paid By</label>
              {cashInHand !== null && (
                <span className={`text-[10px] font-bold ${cashInHand <= 0 ? "text-red-500" : "text-green-600"}`}>
                  {loadingCash ? "Checking..." : `Cash: ৳${cashInHand.toFixed(2)}`}
                </span>
              )}
            </div>
            <DynamicSelect options={staffOptions}
              value={staffOptions.find((o) => o.value === form.paidBy) || null}
              onChange={(o) => setForm({ ...form, paidBy: o?.value || "GENERAL" })} isSearchable={true} />
          </div>
          <div className="relative">
            <label className="text-xs font-bold block mb-1">Amount (৳)</label>
            <input 
              type="number" 
              placeholder="0.00" 
              className={`w-full border p-2 rounded text-sm outline-none ${
                cashInHand !== null && Number(form.amount) > cashInHand
                  ? "border-red-500 focus:border-red-500 bg-red-50"
                  : "focus:border-M-primary-color"
              }`}
              value={form.amount} 
              onChange={(e) => setForm({ ...form, amount: e.target.value })} 
            />
            {cashInHand !== null && Number(form.amount) > cashInHand && (
              <span className="text-[10px] text-red-500 absolute -bottom-4 left-0 whitespace-nowrap">
                Exceeds available cash!
              </span>
            )}
          </div>
          <div>
            <label className="text-xs font-bold block mb-1">Date</label>
            <input type="date" className="w-full border p-2 rounded text-sm outline-none focus:border-M-primary-color"
              value={form.expenseDate} onChange={(e) => setForm({ ...form, expenseDate: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-bold block mb-1">Note</label>
            <input type="text" placeholder="Optional" className="w-full border p-2 rounded text-sm outline-none focus:border-M-primary-color"
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="mt-5">
            <button 
              onClick={handleAdd} 
              disabled={
                submitting || 
                loadingCash || 
                (cashInHand !== null && (cashInHand <= 0 || Number(form.amount) > cashInHand))
              }
              className="w-full bg-M-primary-color/90 hover:bg-M-primary-color text-white text-sm py-2.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200"
              title={cashInHand <= 0 ? "Insufficient cash in hand" : ""}
            >
              <FaPlus size={12} /> {submitting ? "Saving..." : "Add"}
            </button>
          </div>
        </div>

        {/* Filter bar + total */}
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-gray-100">
          <div className="flex gap-1">
            {["daily", "monthly", "all"].map((t) => (
              <button key={t} onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold capitalize ${filterType === t ? "bg-M-primary-color text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                {t}
              </button>
            ))}
          </div>
          {filterType !== "all" && (
            <input type="date" className="border p-1.5 rounded text-sm" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
          )}
          <div className="min-w-[180px]">
            <DynamicSelect
              options={[{ value: "All", label: "All Categories" }, ...CATEGORY_OPTIONS]}
              value={
                [{ value: "All", label: "All Categories" }, ...CATEGORY_OPTIONS].find(
                  (o) => o.value === filterCategory
                ) || null
              }
              onChange={(o) => setFilterCategory(o?.value || "All")}
              isSearchable={false}
            />
          </div>
          <div className="min-w-[200px]">
            <DynamicSelect
              options={filterUserOptions}
              value={filterUserOptions.find((o) => o.value === filterUser) || null}
              onChange={(o) => setFilterUser(o?.value || "ALL")}
              isSearchable={true}
            />
          </div>
          {displayExpenses.length > 0 && (
            <AccountExportOptions
              data={displayExpenses}
              title="Others Expense Report"
              subTitle={getExpenseSubtitle()}
              headers={exportHeaders}
              dataMapper={exportDataMapper}
              footer={expenseFooter}
              printedBy={user?.name || "Staff"}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
            />
          )}
          <div className="ml-auto bg-red-50 border border-red-200 rounded-lg px-4 py-2 flex items-center gap-1">
            <span className="text-[10px] font-black uppercase text-gray-400">Total Expense</span>
            <span className="text-lg font-black text-red-600 ml-2">৳{displayTotal.toFixed(2)}</span>
            <span className="text-[11px] text-gray-400 ml-2">({displayExpenses.length})</span>
          </div>
        </div>

        {/* List */}
        <div className="overflow-x-auto p-4">
          <table className="min-w-[700px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100 text-left text-[11px] uppercase font-medium text-M-heading-color">
                <th className="p-3">Date</th>
                <th className="p-3">Category</th>
                <th className="p-3">Paid By</th>
                <th className="p-3">Note</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-sm">Loading...</td></tr>
              ) : currentData.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-sm">No expenses for this selection.</td></tr>
              ) : (
                currentData.map((ex) => (
                  <tr key={ex.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-600">{new Date(ex.expenseDate).toLocaleDateString("en-GB")}</td>
                    <td className="p-3">
                      <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-[11px] font-bold border border-gray-200">{ex.category}</span>
                    </td>
                    <td className="p-3 text-sm capitalize">
                      {ex.userName
                        ? <span className="text-blue-700">{ex.userName.toLowerCase()}</span>
                        : <span className="text-purple-600 font-semibold">General (Lab Fund)</span>}
                    </td>
                    <td className="p-3 text-sm text-gray-500">{ex.note || "—"}</td>
                    <td className="p-3 text-sm font-bold text-red-500 text-right">৳{Number(ex.amount).toFixed(2)}</td>
                    <td className="p-3 text-center">
                      <button onClick={() => setDeleteId(ex.id)} className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300" title="Delete"><FaTrash size={12} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="print-hidden px-4 pb-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={displayExpenses.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <ConfirmActionModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => handleDelete(deleteId)}
        entityName="expense"
        title="Delete this expense?"
        message="This expense will be permanently removed."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default PathologyOtherExpense;